var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { getFractionDimensions } from './helper';
import { GRID_SIZE } from '../constants/gridConstants';
import { ADJACENT_SNAP_GAP, PIECE_GRID } from '../constants/nmpConstants';
import { getDenominatorFromFractionType } from '../components/SplitFractionTileModal';
export var SNAP_THRESHOLD = 55;
export var CIRCLE_SNAP_THRESHOLD = 160;
export var BORDER_OVERLAP = 0; // 0 = tiles touch edge-to-edge; border-right:none CSS hides the double border
var EPSILON = 1;
export var getGlobalFractionTileScale = function (manipulatives, shapeType) {
    var _a, _b;
    var sameShape = manipulatives
        .filter(function (m) { return m.data.type === 'fractionTiles'; })
        .map(function (m) { return m.data; })
        .filter(function (d) { return d.shapeType === shapeType; });
    var unattached = sameShape.find(function (d) { return !d.numberLineId; });
    if (unattached)
        return unattached.scale;
    var attached = sameShape.find(function (d) { return d.numberLineId; });
    return (_b = (_a = attached === null || attached === void 0 ? void 0 : attached.preSnapScale) !== null && _a !== void 0 ? _a : attached === null || attached === void 0 ? void 0 : attached.scale) !== null && _b !== void 0 ? _b : 1;
};
export var getSnappedPosition = function (currentPos, currentDimensions, currentShapeType, manipulatives, ignoreId, draggedGroup, newTileData) {
    var _a, _b;
    var result = {
        x: currentPos.x,
        y: currentPos.y,
        isSnappedX: false,
        isSnappedY: false,
        rotation: undefined,
        modifications: [],
    };
    var ignoreIds = Array.isArray(ignoreId) ? ignoreId : [ignoreId];
    var otherTiles = manipulatives.filter(function (m) {
        return m.data.type === 'fractionTiles' &&
            !ignoreIds.includes(m.data.id) &&
            m.data.shapeType === currentShapeType;
    });
    var draggedTileData = newTileData ||
        ((_a = manipulatives.find(function (m) { return m.data.id === ignoreIds[0]; })) === null || _a === void 0 ? void 0 : _a.data) ||
        ((_b = otherTiles[0]) === null || _b === void 0 ? void 0 : _b.data);
    if (currentShapeType === 'circle') {
        var snap = solveCircleSnap(currentPos, currentDimensions, otherTiles, draggedTileData);
        if (snap)
            return snap;
    }
    else {
        var snap = solveRectangleSnap(currentPos, currentDimensions, otherTiles, draggedGroup);
        if (snap)
            return snap;
    }
    return result;
};
var EDGE_TOUCH_TOL = 5;
/**
 * Determines whether `childPos`/`childDims` (a rectangle tile) is edge-to-edge
 * touching `parentData` (another rectangle tile), and if so which side of the
 * parent it touches and at which segment of the parent's fraction division.
 * Returns null when the two rectangles aren't actually touching along an edge.
 */
export var computeSnapRelation = function (childPos, childDims, parentData) {
    var _a, _b;
    var childLeft = childPos.x;
    var childRight = childPos.x + childDims.width;
    var childTop = childPos.y;
    var childBottom = childPos.y + childDims.height;
    var pLeft = parentData.center.x;
    var pRight = parentData.center.x + parentData.dimensions.width;
    var pTop = parentData.center.y;
    var pBottom = parentData.center.y + parentData.dimensions.height;
    var vertOverlap = childBottom > pTop + EPSILON && childTop < pBottom - EPSILON;
    var horizOverlap = childRight > pLeft + EPSILON && childLeft < pRight - EPSILON;
    var edge = null;
    if (vertOverlap && Math.abs(childRight - pLeft) < EDGE_TOUCH_TOL)
        edge = 'left';
    else if (vertOverlap && Math.abs(childLeft - pRight) < EDGE_TOUCH_TOL)
        edge = 'right';
    else if (horizOverlap && Math.abs(childBottom - pTop) < EDGE_TOUCH_TOL)
        edge = 'top';
    else if (horizOverlap && Math.abs(childTop - pBottom) < EDGE_TOUCH_TOL)
        edge = 'bottom';
    if (!edge) {
        return null;
    }
    var denom = (_b = (_a = parentData.customDenominator) !== null && _a !== void 0 ? _a : getDenominatorFromFractionType(parentData.fractionSize)) !== null && _b !== void 0 ? _b : 1;
    // segmentIndex is stored as a CONTINUOUS offset in units of "one parent segment"
    // (parentWidth / denom), not rounded/clamped to an integer segment. Rounding to
    // the nearest of `denom` segments collapses distinct children onto the same
    // segment whenever the parent is a whole/undivided tile (denom === 1) acting as
    // a hub with several tiles independently touching the same edge at different
    // offsets — e.g. three different tiles all snapped to one undivided tile's
    // bottom edge at x=1310/1706/1772 all rounded to segmentIndex 0 and collapsed
    // onto the same x after scaling. Keeping it continuous preserves each child's
    // real relative offset while still scaling proportionally with the parent.
    var segW = parentData.dimensions.width / (denom || 1);
    // segmentIndex * segW must equal (childLeft - parentLeft) so that the scale walk's
    // `parentPos.x + rel.segmentIndex * segW` reproduces the child's actual left-edge
    // offset from the parent, not just its nearest fraction segment.
    var segmentIndex = (childLeft - pLeft) / segW;
    // Right-aligned dock: the child's right edge is flush with the parent's
    // right edge while its left edge is NOT flush with the parent's left. When
    // both are flush (equal widths) left-anchoring reproduces the same x, so
    // there's nothing to record.
    var alignRight = (edge === 'top' || edge === 'bottom') &&
        Math.abs(childRight - pRight) < EDGE_TOUCH_TOL &&
        Math.abs(childLeft - pLeft) >= EDGE_TOUCH_TOL;
    return { parentId: parentData.id, edge: edge, segmentIndex: segmentIndex, segmentCount: denom, alignRight: alignRight };
};
/**
 * Finds the first rectangle tile among `candidates` that `childPos`/`childDims`
 * is edge-to-edge touching, returning the computed snap relation for it.
 * Returns undefined when no candidate is touching (the caller should then
 * clear any previously-stored `snappedTo` rather than leave it stale).
 */
export var findTouchingParent = function (childPos, childDims, candidates) {
    for (var _i = 0, candidates_1 = candidates; _i < candidates_1.length; _i++) {
        var candidate = candidates_1[_i];
        if (candidate.shapeType !== 'rectangle')
            continue;
        var rel = computeSnapRelation(childPos, childDims, candidate);
        if (rel)
            return rel;
    }
    return undefined;
};
/**
 * Two-pass dock solve for rectangle fraction tiles.
 *
 * A dock position is only valid for the width/height it was solved with: a
 * right-aligned or corner-aligned dock computed at one size and then rescaled
 * to another lands in the wrong place (the rescale keeps the top-left, so the
 * right/bottom edges slide off the tile they were flush with). And a tile
 * docking next to a number-line-attached neighbour MUST adopt that
 * neighbour's scale, so its final size is not known until the dock target is.
 *
 * Chicken-and-egg, resolved by solving twice:
 *   1. Solve at `baselineDims` purely to learn WHICH tile would be docked to
 *      (`SnapResult.dockedToId`).
 *   2. Size the dragged tile to that tile's scale and solve AGAIN.
 * The second result is the one to render as the ghost and to commit on drop,
 * together with the `dimensions` it was solved with — so the preview, the drop
 * position and the final size all agree by construction, with no
 * after-the-fact scale correction to knock the tile off its dock.
 *
 * The previous approach — pre-sizing only when a `findAdjacentFractionTileScale`
 * found a neighbour — silently did nothing across most of the catch radius:
 * that check needs edge contact within EPSILON * 5 (5px) at the raw cursor
 * position, while the solver docks within SNAP_THRESHOLD (55px). Drops in the
 * 5-55px band got a dock solved at the wrong size.
 *
 * Re-solving cannot loop: pass 2 is solved at the target's own scale, so a
 * third pass would return the same target and the same size.
 */
export var solveDockMatchingScale = function (pos, baselineDims, unscaledDimensions, manipulatives, ignoreId, draggedGroup, newTileData) {
    var _a, _b;
    var first = getSnappedPosition(pos, baselineDims, 'rectangle', manipulatives, ignoreId, draggedGroup, newTileData);
    var dockedTo = first.dockedToId
        ? (_a = manipulatives.find(function (m) { return m.data.id === first.dockedToId; })) === null || _a === void 0 ? void 0 : _a.data
        : undefined;
    if (!dockedTo)
        return { snap: first, dimensions: baselineDims };
    var matchedDims = {
        width: unscaledDimensions.width * dockedTo.scale,
        height: unscaledDimensions.height * dockedTo.scale,
    };
    // Already the right size — the first solve stands.
    if (Math.abs(matchedDims.width - baselineDims.width) < 0.01 &&
        Math.abs(matchedDims.height - baselineDims.height) < 0.01) {
        return { snap: first, dimensions: baselineDims, dockedTo: dockedTo };
    }
    // `draggedGroup` must be rescaled by the same factor, NOT passed through:
    // when a group is supplied (it is, even for a single dragged tile — the
    // leader is a member), solveRectangleSnap takes checkLeft/checkRight and
    // virtualItem.dimensions from the GROUP's dimensions and ignores the `dims`
    // argument entirely. Handing pass 2 the corrected `dims` with stale group
    // dimensions makes it solve every candidate at the old width — a
    // right-align lands (oldWidth - newWidth) short of the parent's right edge.
    var groupScaleFactor = baselineDims.width > 0 ? matchedDims.width / baselineDims.width : 1;
    var scaledGroup = draggedGroup === null || draggedGroup === void 0 ? void 0 : draggedGroup.map(function (g) { return (__assign(__assign({}, g), { offset: { x: g.offset.x * groupScaleFactor, y: g.offset.y * groupScaleFactor }, dimensions: {
            width: g.dimensions.width * groupScaleFactor,
            height: g.dimensions.height * groupScaleFactor,
        } })); });
    var second = getSnappedPosition(pos, matchedDims, 'rectangle', manipulatives, ignoreId, scaledGroup, newTileData);
    // At the corrected size the tile may no longer reach anything (a narrower
    // tile has less overlap to dock with). Falling back to the first solve
    // would re-introduce the size/position mismatch, so report "no dock" and
    // let the caller keep the tile where the user dropped it.
    if (!second.isSnappedX && !second.isSnappedY) {
        return { snap: second, dimensions: matchedDims };
    }
    var secondDockedTo = second.dockedToId
        ? (_b = manipulatives.find(function (m) { return m.data.id === second.dockedToId; })) === null || _b === void 0 ? void 0 : _b.data
        : undefined;
    return { snap: second, dimensions: matchedDims, dockedTo: secondDockedTo !== null && secondDockedTo !== void 0 ? secondDockedTo : dockedTo };
};
/**
 * Parent-first walk over every rectangle tile's stored `snappedTo` relation,
 * resolving each tile's `center`/`dimensions`/`scale`/`numberLineId` from its
 * parent's CURRENT (already-updated) values in `rectTiles` — not just
 * position (as the scalePlus/scaleMinus "scale walk" in
 * FractionTileController.tsx does), but scale too, since a tile can also
 * diverge in scale independently of its `snappedTo` siblings by attaching to
 * a number line. Without this, a tile stacked on a number-line-attached
 * parent (or a grandparent, transitively) keeps whatever scale it had at the
 * moment it was originally docked, and drifts out of proportion the next
 * time the parent's number-line-forced scale changes (attach/detach/rescale).
 *
 * A tile that itself has its own `numberLineId` keeps its own scale (it is
 * independently authoritative, not following its `snappedTo` parent). Every
 * other tile adopts its resolved parent's scale/dimensions/numberLineId.
 */
export var resolveSnappedChainGeometry = function (rectTiles) {
    var byId = new Map(rectTiles.map(function (t) { return [t.id, t]; }));
    var resolved = new Map();
    var visiting = new Set();
    var resolve = function (tile) {
        var _a;
        var cached = resolved.get(tile.id);
        if (cached)
            return cached;
        var asIs = {
            center: tile.center,
            dimensions: tile.dimensions,
            scale: tile.scale,
            numberLineId: tile.numberLineId,
        };
        if (((_a = tile.lock) === null || _a === void 0 ? void 0 : _a.positioning) || visiting.has(tile.id)) {
            resolved.set(tile.id, asIs);
            return asIs;
        }
        var rel = tile.snappedTo;
        var parent = rel && byId.get(rel.parentId);
        if (!rel || !parent || tile.numberLineId) {
            // Unsnapped, parent no longer present, or this tile is itself
            // number-line-attached — its own scale is already authoritative.
            resolved.set(tile.id, asIs);
            return asIs;
        }
        visiting.add(tile.id);
        var parentResolved = resolve(parent);
        visiting.delete(tile.id);
        var baseWidth = tile.dimensions.width / (tile.scale || 1);
        var baseHeight = tile.dimensions.height / (tile.scale || 1);
        var myScale = parentResolved.scale;
        var myDims = { width: baseWidth * myScale, height: baseHeight * myScale };
        var segW = parentResolved.dimensions.width / (rel.segmentCount || 1);
        // For 'top'/'bottom' the child's x is a free parameter, so honour the
        // anchoring the dock was actually made with: a right-aligned dock must
        // keep its right edge flush with the parent's as the child's own width
        // changes, not keep its left edge pinned (see `alignRight`).
        var stackedX = rel.alignRight
            ? parentResolved.center.x + parentResolved.dimensions.width - myDims.width
            : parentResolved.center.x + rel.segmentIndex * segW;
        var center;
        if (rel.edge === 'left')
            center = { x: parentResolved.center.x - myDims.width, y: parentResolved.center.y };
        else if (rel.edge === 'right')
            center = {
                x: parentResolved.center.x + parentResolved.dimensions.width,
                y: parentResolved.center.y,
            };
        else if (rel.edge === 'bottom')
            center = {
                x: stackedX,
                y: parentResolved.center.y + parentResolved.dimensions.height,
            };
        else
            center = {
                x: stackedX,
                y: parentResolved.center.y - myDims.height,
            };
        var result = {
            center: center,
            dimensions: myDims,
            scale: myScale,
            numberLineId: parentResolved.numberLineId,
        };
        resolved.set(tile.id, result);
        return result;
    };
    rectTiles.forEach(resolve);
    return resolved;
};
/**
 * Detaches a FractionTile from a number line it is leaving (deletion,
 * detach-on-move, etc.): clears `numberLineId`/`preSnapScale` and restores
 * `scale`/`dimensions` to the shared global scale it was tracking via
 * `preSnapScale` while attached (falling back to its current NL-forced scale
 * if none was ever recorded). `center` is left untouched — the tile stays
 * exactly where it was, it just stops being NL-driven so scalePlus/scaleMinus
 * can resize it again.
 */
export var detachFractionTileFromNumberLine = function (tileData) {
    var _a, _b, _c;
    var restoredScale = (_b = (_a = tileData.preSnapScale) !== null && _a !== void 0 ? _a : tileData.scale) !== null && _b !== void 0 ? _b : 1;
    var isGlued = !!(tileData.gluedFrom && tileData.gluedFrom.length > 0);
    var baseDim = (_c = tileData.defaultDimensions) !== null && _c !== void 0 ? _c : (function () {
        var _a, _b;
        var config = getFractionDimensions(tileData.fractionSize);
        return { width: (_b = (_a = config === null || config === void 0 ? void 0 : config.rectangle) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 660, height: 55 };
    })();
    var baseWidth = tileData.shapeType === 'circle' && !isGlued ? 300 : baseDim.width;
    var baseHeight = tileData.shapeType === 'circle' && !isGlued ? 300 : baseDim.height;
    return __assign(__assign({}, tileData), { scale: restoredScale, dimensions: { width: baseWidth * restoredScale, height: baseHeight * restoredScale }, numberLineId: undefined, preSnapScale: undefined });
};
/**
 * Computes the full set of FractionTile data updates needed when one or more
 * NumberLines go away for their attached tiles — deleted, or (detach-on-move)
 * moved out from under them: every tile whose `numberLineId` matches is
 * detached (see `detachFractionTileFromNumberLine`), AND every other
 * rectangle tile stacked/docked onto it via `snappedTo` is repositioned to
 * match — otherwise the chain's flush alignment breaks the moment a tile's
 * dimensions change out from under its siblings' frozen `center` values.
 * Reuses `resolveSnappedChainGeometry` (the same parent-first chain walk
 * `FractionTileController.tsx`'s scalePlus/scaleMinus uses), which is
 * explicitly designed for "a number-line-forced scale just changed
 * (attach/detach/rescale)" per its own doc comment above.
 *
 * Returns a Map of tile id -> new data, for every tile whose center,
 * dimensions, scale, or numberLineId actually changed. Circle tiles never
 * attach to number lines, so they're excluded from the chain walk and
 * handled with a simple independent detach if one somehow has a stale
 * `numberLineId`.
 */
export var computeFractionTileNumberLineDetachUpdates = function (currentManipulatives, isDetachedNumberLineId) {
    var updates = new Map();
    var allTileData = currentManipulatives
        .filter(function (m) { return m.data.type === 'fractionTiles'; })
        .map(function (m) { return m.data; });
    var isAttachedToDetachedNL = function (t) { var _a; return isDetachedNumberLineId((_a = t.numberLineId) !== null && _a !== void 0 ? _a : ''); };
    allTileData
        .filter(function (t) { return t.shapeType === 'circle' && isAttachedToDetachedNL(t); })
        .forEach(function (t) { return updates.set(t.id, detachFractionTileFromNumberLine(t)); });
    var rectTiles = allTileData.filter(function (t) { return t.shapeType === 'rectangle'; });
    if (!rectTiles.some(isAttachedToDetachedNL))
        return updates;
    var seeded = rectTiles.map(function (t) {
        return isAttachedToDetachedNL(t) ? detachFractionTileFromNumberLine(t) : t;
    });
    var resolved = resolveSnappedChainGeometry(seeded);
    rectTiles.forEach(function (original) {
        var seed = seeded.find(function (s) { return s.id === original.id; });
        var r = resolved.get(original.id);
        if (!seed || !r)
            return;
        var changed = Math.abs(r.center.x - original.center.x) > 0.1 ||
            Math.abs(r.center.y - original.center.y) > 0.1 ||
            Math.abs(r.scale - original.scale) > 1e-6 ||
            r.numberLineId !== original.numberLineId;
        if (!changed)
            return;
        updates.set(original.id, __assign(__assign({}, seed), { center: r.center, dimensions: r.dimensions, scale: r.scale, numberLineId: r.numberLineId }));
    });
    return updates;
};
var solveCircleSnap = function (pos, dims, targets, draggedData) {
    var _a, _b, _c;
    var myCenterX = pos.x + dims.width / 2;
    var myCenterY = pos.y + dims.height / 2;
    // Proximity gate: CIRCLE_SNAP_THRESHOLD (center-to-center) handles
    // both adjacency and stacking. No bounding-box edge gate needed —
    // sectors snap by sharing a center, not by edge adjacency.
    // Find closest target within threshold
    var closestTarget = null;
    var minDistSq = Math.pow(CIRCLE_SNAP_THRESHOLD, 2);
    for (var _i = 0, targets_1 = targets; _i < targets_1.length; _i++) {
        var m = targets_1[_i];
        var t = m.data;
        var tCenterX = t.center.x + t.dimensions.width / 2;
        var tCenterY = t.center.y + t.dimensions.height / 2;
        var distSq = Math.pow(myCenterX - tCenterX, 2) + Math.pow(myCenterY - tCenterY, 2);
        if (distSq < minDistSq) {
            minDistSq = distSq;
            closestTarget = t;
        }
    }
    if (!closestTarget)
        return null;
    // Target found
    var targetCenterX = closestTarget.center.x + closestTarget.dimensions.width / 2;
    var targetCenterY = closestTarget.center.y + closestTarget.dimensions.height / 2;
    var snappedX = targetCenterX - dims.width / 2;
    var snappedY = targetCenterY - dims.height / 2;
    // --- GAP-BASED SNAPPING LOGIC ---
    var tilesAtCenter = targets.filter(function (m) {
        var d = m.data;
        var cx = d.center.x + d.dimensions.width / 2;
        var cy = d.center.y + d.dimensions.height / 2;
        return Math.abs(cx - targetCenterX) < 10 && Math.abs(cy - targetCenterY) < 10;
    });
    var fractionSize = (draggedData === null || draggedData === void 0 ? void 0 : draggedData.fractionSize) || 'fraction-1';
    var myConfig = ((_a = getFractionDimensions(fractionSize)) === null || _a === void 0 ? void 0 : _a.circle) || {
        start: 0,
        sweep: 360,
    };
    var myRotation = (_b = draggedData === null || draggedData === void 0 ? void 0 : draggedData.rotation) !== null && _b !== void 0 ? _b : 0;
    var mySweep = (_c = draggedData === null || draggedData === void 0 ? void 0 : draggedData.customSweep) !== null && _c !== void 0 ? _c : myConfig.sweep;
    if (mySweep >= 360) {
        return {
            x: snappedX,
            y: snappedY,
            isSnappedX: true,
            isSnappedY: true,
            rotation: 0,
            modifications: [],
        };
    }
    // 1. Get all occupied segments [start, end]
    var segments = tilesAtCenter.map(function (m) {
        var _a, _b, _c, _d;
        var d = m.data;
        var c = ((_a = getFractionDimensions(d.fractionSize)) === null || _a === void 0 ? void 0 : _a.circle) || {
            start: 0,
            sweep: 360,
        };
        var sw = (_b = d.customSweep) !== null && _b !== void 0 ? _b : c.sweep;
        // Glued tiles store rotation as the absolute start angle.
        // Normal tiles store rotation as an offset applied on top of c.start.
        var s = d.customSweep != null
            ? ((((_c = d.rotation) !== null && _c !== void 0 ? _c : 0) % 360) + 360) % 360
            : (((c.start + ((_d = d.rotation) !== null && _d !== void 0 ? _d : 0)) % 360) + 360) % 360;
        return { start: s, end: (s + sw) % 360, sweep: sw };
    });
    // 2. Identify Gaps
    // First, normalise segments to their angular union so that tiles stacked in
    // multiple layers (duplicate segments at the same angle) don't produce phantom
    // gaps. We use a 1°-resolution boolean array for simplicity; all standard
    // fraction sweeps (30°, 36°, 45°, 60°, 72°, 90°, 120°, 180°, 360°) are
    // integer degrees, so 1° resolution is exact.
    var covered = new Array(360).fill(0);
    for (var _d = 0, segments_1 = segments; _d < segments_1.length; _d++) {
        var seg = segments_1[_d];
        var sw = Math.min(Math.ceil(seg.sweep), 360);
        for (var i = 0; i < sw; i++) {
            covered[Math.round((seg.start + i) % 360)] = 1;
        }
    }
    var normalizedSegments = [];
    var inSeg = false;
    var normSegStart = 0;
    for (var i = 0; i < 360; i++) {
        if (covered[i] && !inSeg) {
            inSeg = true;
            normSegStart = i;
        }
        else if (!covered[i] && inSeg) {
            inSeg = false;
            normalizedSegments.push({ start: normSegStart, end: i, sweep: i - normSegStart });
        }
    }
    if (inSeg)
        normalizedSegments.push({ start: normSegStart, end: 0, sweep: 360 - normSegStart });
    var sorted = __spreadArray([], normalizedSegments, true).sort(function (a, b) { return a.start - b.start; });
    var gaps = [];
    if (sorted.length === 0) {
        gaps.push({ start: 0, end: 0, sweep: 360 });
    }
    else {
        for (var i = 0; i < sorted.length; i++) {
            var current = sorted[i];
            var next = sorted[(i + 1) % sorted.length];
            var gapStart = current.end;
            var gapEnd = next.start;
            var gapSweep = (gapEnd - gapStart + 360) % 360;
            if (gapSweep > EPSILON) {
                gaps.push({ start: gapStart, end: gapEnd, sweep: gapSweep });
            }
        }
    }
    // 3. Find Best Snap Point
    var dragAngle = (Math.atan2(myCenterY - targetCenterY, myCenterX - targetCenterX) * 180) / Math.PI;
    dragAngle = ((dragAngle % 360) + 360) % 360;
    var bestRotation = myRotation;
    var minAngularDiff = 360;
    var foundSnap = false;
    // Whether the dragged tile is a glued tile (rotation = absolute start, not offset)
    var draggedIsGlued = (draggedData === null || draggedData === void 0 ? void 0 : draggedData.customSweep) != null;
    // Option A / B: fill a gap (existing behaviour)
    for (var _e = 0, gaps_1 = gaps; _e < gaps_1.length; _e++) {
        var gap = gaps_1[_e];
        if (gap.sweep + EPSILON >= mySweep) {
            // Option A: Align my start to gap start
            var rotA = draggedIsGlued
                ? ((gap.start % 360) + 360) % 360
                : (((gap.start - myConfig.start) % 360) + 360) % 360;
            var midA = (gap.start + mySweep / 2) % 360;
            var diffA = Math.abs(((dragAngle - midA + 540) % 360) - 180);
            if (diffA < minAngularDiff) {
                minAngularDiff = diffA;
                bestRotation = rotA;
                foundSnap = true;
            }
            // Option B: Align my end to gap end
            var myStartForB = (gap.end - mySweep + 360) % 360;
            var rotB = draggedIsGlued
                ? ((myStartForB % 360) + 360) % 360
                : (((myStartForB - myConfig.start) % 360) + 360) % 360;
            var midB = (myStartForB + mySweep / 2) % 360;
            var diffB = Math.abs(((dragAngle - midB + 540) % 360) - 180);
            if (diffB < minAngularDiff) {
                minAngularDiff = diffB;
                bestRotation = rotB;
                foundSnap = true;
            }
        }
    }
    // Option C: stack on top of an existing sector.
    // This competes with gap-fill: the drag angle decides which wins.
    // If the user drags toward an occupied sector the tile stacks there;
    // if toward an empty gap it fills the gap.
    for (var _f = 0, normalizedSegments_1 = normalizedSegments; _f < normalizedSegments_1.length; _f++) {
        var seg = normalizedSegments_1[_f];
        var rotC = draggedIsGlued
            ? ((seg.start % 360) + 360) % 360
            : (((seg.start - myConfig.start) % 360) + 360) % 360;
        // Compare angular distance of drag angle to the midpoint of where
        // the dragged tile would sit when start-aligned with this segment.
        var midC = (seg.start + mySweep / 2) % 360;
        var diffC = Math.abs(((dragAngle - midC + 540) % 360) - 180);
        if (diffC < minAngularDiff) {
            minAngularDiff = diffC;
            bestRotation = rotC;
            foundSnap = true;
        }
    }
    if (!foundSnap) {
        // Fallback: no gap and no existing sector matched — shouldn't normally
        // reach here, but return null so the caller can handle it gracefully.
        return null;
    }
    return {
        x: snappedX,
        y: snappedY,
        isSnappedX: true,
        isSnappedY: true,
        rotation: bestRotation,
        modifications: [],
    };
};
var solveRectangleSnap = function (pos, dims, targets, draggedGroup) {
    var _a, _b, _c, _d;
    var myCenterY = pos.y + dims.height / 2;
    // Edge-to-edge proximity gate: only enter snap logic when at least one
    // edge of the dragged tile (or group) is within EDGE_SNAP_THRESHOLD of
    // a target tile's edge. This allows stacking when no edges are close.
    // Match SNAP_THRESHOLD so the gate doesn't reject valid drops that are
    // within the actual snap distance (previously 10 caused misses at 11-54px).
    var EDGE_SNAP_THRESHOLD = SNAP_THRESHOLD;
    var checkLeft = pos.x;
    var checkRight = pos.x + dims.width;
    var checkTop = pos.y;
    var checkBottom = pos.y + dims.height;
    if (draggedGroup && draggedGroup.length > 0) {
        var minX = Math.min.apply(Math, draggedGroup.map(function (g) { return g.offset.x; }));
        var maxX = Math.max.apply(Math, draggedGroup.map(function (g) { return g.offset.x + g.dimensions.width; }));
        var minY = Math.min.apply(Math, draggedGroup.map(function (g) { return g.offset.y; }));
        var maxY = Math.max.apply(Math, draggedGroup.map(function (g) { return g.offset.y + g.dimensions.height; }));
        checkLeft = pos.x + minX;
        checkRight = pos.x + maxX;
        checkTop = pos.y + minY;
        checkBottom = pos.y + maxY;
    }
    var hasCloseEdge = false;
    for (var _i = 0, targets_2 = targets; _i < targets_2.length; _i++) {
        var m = targets_2[_i];
        var t = m.data;
        var tLeft = t.center.x;
        var tRight = t.center.x + t.dimensions.width;
        var tTop = t.center.y;
        var tBottom = t.center.y + t.dimensions.height;
        var vertOverlap = checkBottom > tTop - EDGE_SNAP_THRESHOLD && checkTop < tBottom + EDGE_SNAP_THRESHOLD;
        var horizOverlap = checkRight > tLeft - EDGE_SNAP_THRESHOLD && checkLeft < tRight + EDGE_SNAP_THRESHOLD;
        // Gap = actual space between opposite edges.
        // When separated, one gap is positive (real distance), the other is negative (clamped to 0).
        // Use Math.max to pick the real gap. When overlapping, both clamp to 0.
        var hDist = Math.max(0, tLeft - checkRight, // positive when dragged is left of target
        checkLeft - tRight);
        var vDist = Math.max(0, tTop - checkBottom, // positive when dragged is above target
        checkTop - tBottom);
        var hClose = vertOverlap && hDist <= EDGE_SNAP_THRESHOLD;
        var vClose = horizOverlap && vDist <= EDGE_SNAP_THRESHOLD;
        if (hClose || vClose) {
            hasCloseEdge = true;
            break;
        }
    }
    // Determine if the dragged tile is above or below all targets.
    // If so, prefer above/below anchors over same-row to prevent
    // horizontal snapping when approaching vertically.
    var isAboveOrBelow = false;
    if (hasCloseEdge) {
        var allTargetTop = Math.min.apply(Math, targets.map(function (m) { return m.data.center.y; }));
        var allTargetBottom = Math.max.apply(Math, targets.map(function (m) {
            var d = m.data;
            return d.center.y + d.dimensions.height;
        }));
        isAboveOrBelow = myCenterY < allTargetTop || myCenterY > allTargetBottom;
    }
    // Compute groupLeftOffset early — needed by both the stacking check and the
    // chain-insertion logic below.
    var groupLeftOffset = 0;
    if (draggedGroup && draggedGroup.length > 0) {
        var rowGroup = draggedGroup.filter(function (g) { return Math.abs(g.offset.y) < 10; });
        if (rowGroup.length > 0) {
            groupLeftOffset = Math.min.apply(Math, rowGroup.map(function (g) { return g.offset.x; }));
        }
    }
    var _loop_1 = function (m) {
        var t = m.data;
        if (pos.x >= t.center.x &&
            pos.x <= t.center.x + t.dimensions.width &&
            pos.y >= t.center.y &&
            pos.y <= t.center.y + t.dimensions.height) {
            var stackY_1 = t.center.y;
            // All tiles in this row (same Y), sorted left to right.
            var allRowTiles = targets
                .map(function (m2) { return m2.data; })
                .filter(function (d) { return Math.abs(d.center.y - stackY_1) < 5; })
                .sort(function (a, b) { return a.center.x - b.center.x; });
            var leftMostX = allRowTiles.length > 0 ? allRowTiles[0].center.x : t.center.x;
            // Snap candidates: left edge of leftmost + right edge of every tile in
            // the row + immediately-before-the-triggering-tile + flush-overlap
            // with the triggering tile itself. `leftMostX` is the leftmost tile of
            // the WHOLE row, which is only the tile under the cursor when that
            // tile happens to be the row's leftmost member (e.g. a lone tile like
            // "1") — for any other tile in a row with a gap (e.g. a corner-aligned
            // tile far from the row's other members), `leftMostX` refers to a
            // completely different, distant tile. Without an explicit
            // "overlap this tile" candidate, hovering directly over such a tile
            // had no flush-stack option at all.
            var snapCandidates = [
                leftMostX - groupLeftOffset,
                t.center.x - dims.width - groupLeftOffset,
                t.center.x - groupLeftOffset,
            ];
            for (var _t = 0, allRowTiles_1 = allRowTiles; _t < allRowTiles_1.length; _t++) {
                var st = allRowTiles_1[_t];
                snapCandidates.push(st.center.x + st.dimensions.width - groupLeftOffset);
            }
            // Pick the candidate whose snapped position is closest to where the tile currently is.
            var bestSnapX = snapCandidates[0];
            var minDist = Math.abs(pos.x - snapCandidates[0]);
            for (var _u = 0, snapCandidates_1 = snapCandidates; _u < snapCandidates_1.length; _u++) {
                var candidate = snapCandidates_1[_u];
                var dist = Math.abs(pos.x - candidate);
                if (dist < minDist) {
                    minDist = dist;
                    bestSnapX = candidate;
                }
            }
            return { value: {
                    x: bestSnapX,
                    y: stackY_1,
                    isSnappedX: true,
                    isSnappedY: true,
                    rotation: undefined,
                    modifications: [],
                    // Stacking onto `t` — the tile the cursor is inside.
                    dockedToId: t.id,
                } };
        }
    };
    // Stacking: if dragged tile's center is inside a target tile,
    // snap to the closest edge among all tiles already in that row
    // (left of leftmost OR right edge of any stacked tile).
    for (var _e = 0, targets_3 = targets; _e < targets_3.length; _e++) {
        var m = targets_3[_e];
        var state_1 = _loop_1(m);
        if (typeof state_1 === "object")
            return state_1.value;
    }
    if (!hasCloseEdge) {
        return null;
    }
    // Find closest target and best anchor (current row, top, or bottom)
    var bestTargetY = null;
    var minDy = SNAP_THRESHOLD;
    var minDx = Infinity;
    var closestTileData = null;
    for (var _f = 0, targets_4 = targets; _f < targets_4.length; _f++) {
        var m = targets_4[_f];
        var t = m.data;
        // Possible vertical alignment anchors based on actual tile heights
        // - t.center.y: Align on the same row (top edges aligned)
        // - t.center.y - dims.height: Place current tile exactly ABOVE target
        // - t.center.y + t.dimensions.height: Place current tile exactly BELOW target
        var aboveY = t.center.y - dims.height;
        var belowY = t.center.y + t.dimensions.height;
        var sameRowY = t.center.y;
        // When the edge gate detected vertical adjacency (above/below),
        // only consider above/below anchors — skip same-row to prevent
        // square tiles from always snapping sideways.
        var possibleYAnchors = isAboveOrBelow ? [aboveY, belowY] : [sameRowY, aboveY, belowY];
        // Left-edge based horizontal distance — using tile centers here would
        // bias toward the middle of the target when the dragged tile's width
        // differs a lot from the target's (e.g. a wide "1" tile dropped above
        // a row of narrow "1/12" tiles lands its center several tiles in).
        var dx = Math.abs(pos.x - t.center.x);
        for (var _g = 0, possibleYAnchors_1 = possibleYAnchors; _g < possibleYAnchors_1.length; _g++) {
            var anchorY = possibleYAnchors_1[_g];
            var dy = Math.abs(pos.y - anchorY);
            if (dy < minDy - EPSILON) {
                // Strictly closer vertically — always pick
                minDy = dy;
                minDx = dx;
                bestTargetY = anchorY;
                closestTileData = t;
            }
            else if (dy < minDy + EPSILON && dx < minDx) {
                // Same vertical distance — pick the one closer horizontally
                minDy = dy;
                minDx = dx;
                bestTargetY = anchorY;
                closestTileData = t;
            }
        }
    }
    if (!closestTileData || bestTargetY === null) {
        return null;
    }
    var targetY = bestTargetY;
    var ROW_Y_TOL = 5; // strict y-alignment tolerance
    var CHAIN_TOL = 5; // adjacency tolerance for connected chains
    // Strict row detection: only include tiles whose top edge is closely aligned
    // with targetY (prevents pulling in tiles from adjacent rows).
    // Deduplicate stacked tiles: if a tile's X range overlaps an already-kept
    // tile's X range, it's stacked on top — skip it so the insertion logic
    // doesn't spread stacked tiles out horizontally.
    var rowTilesRaw = targets
        .map(function (t) { return t.data; })
        .filter(function (d) { return Math.abs(d.center.y - targetY) < ROW_Y_TOL; })
        .sort(function (a, b) { return a.center.x - b.center.x; });
    var rowTiles = [];
    var _loop_2 = function (tile) {
        var tileLeft = tile.center.x;
        var tileRight = tile.center.x + tile.dimensions.width;
        var isStacked = rowTiles.some(function (existing) {
            var exLeft = existing.center.x;
            var exRight = existing.center.x + existing.dimensions.width;
            // Overlapping X ranges = stacked (ignore BORDER_OVERLAP between adjacent tiles)
            var overlap = Math.min(tileRight, exRight) - Math.max(tileLeft, exLeft);
            return overlap > BORDER_OVERLAP + EPSILON;
        });
        if (!isStacked)
            rowTiles.push(tile);
    };
    for (var _h = 0, rowTilesRaw_1 = rowTilesRaw; _h < rowTilesRaw_1.length; _h++) {
        var tile = rowTilesRaw_1[_h];
        _loop_2(tile);
    }
    var virtualItem = { id: 'NEW', center: { x: 0, y: targetY }, dimensions: dims };
    if (draggedGroup && draggedGroup.length > 0) {
        var rowGroup = draggedGroup.filter(function (g) { return Math.abs(g.offset.y) < 10; });
        if (rowGroup.length > 0) {
            // Find leftmost and rightmost relative to leader
            var minX = Math.min.apply(Math, rowGroup.map(function (g) { return g.offset.x; }));
            var maxX = Math.max.apply(Math, rowGroup.map(function (g) { return g.offset.x + g.dimensions.width; }));
            var totalWidth = maxX - minX;
            virtualItem = {
                id: 'NEW_GROUP',
                center: { x: 0, y: targetY },
                dimensions: { width: totalWidth, height: dims.height },
            };
        }
    }
    // Horizontal snapping logic
    // If there ARE tiles in this target row, we use the insertion logic
    if (rowTiles.length > 0) {
        // --- CONNECTED CHAIN DETECTION ---
        // Build chains of adjacent tiles so we only snap into the nearest chain,
        // not ALL tiles in the row (prevents pulling in distant tiles).
        var chains = [];
        var currentChain = [rowTiles[0]];
        for (var i = 1; i < rowTiles.length; i++) {
            var prevRight = rowTiles[i - 1].center.x + rowTiles[i - 1].dimensions.width;
            if (Math.abs(prevRight - rowTiles[i].center.x) < CHAIN_TOL) {
                currentChain.push(rowTiles[i]);
            }
            else {
                chains.push(currentChain);
                currentChain = [rowTiles[i]];
            }
        }
        chains.push(currentChain);
        // Find the chain closest to the dragged tile
        var bestChain = chains[0];
        var bestDist = Infinity;
        for (var _j = 0, chains_1 = chains; _j < chains_1.length; _j++) {
            var chain = chains_1[_j];
            var chainLeft_1 = chain[0].center.x;
            var chainRight_1 = chain[chain.length - 1].center.x + chain[chain.length - 1].dimensions.width;
            // Bounding-box overlap distance (using the dragged tile's actual left/
            // right edges), not center distance — a wide dragged tile overlapping
            // a chain by its edges should count as "at" that chain even if its
            // center falls outside it.
            var dist = void 0;
            if (checkRight < chainLeft_1) {
                dist = chainLeft_1 - checkRight;
            }
            else if (checkLeft > chainRight_1) {
                dist = checkLeft - chainRight_1;
            }
            else {
                dist = 0;
            }
            if (dist < bestDist) {
                bestDist = dist;
                bestChain = chain;
            }
        }
        // Use the closest chain for insertion (not all rowTiles)
        var snapTiles = bestChain;
        // Left-edge based: insert before the first tile whose left edge is to
        // the right of the dragged tile's left edge. For equal-width tiles this
        // matches the old center-based midpoint comparison exactly; for tiles of
        // very different widths (e.g. a "1" tile over a row of "1/12" tiles) it
        // correctly inserts at the position implied by the dragged tile's own
        // left edge instead of drifting toward wherever its center lands.
        var insertIndex = 0;
        for (var i = 0; i < snapTiles.length; i++) {
            var tile = snapTiles[i];
            if (pos.x < tile.center.x) {
                insertIndex = i;
                break;
            }
            insertIndex = i + 1;
        }
        // Bounds of the snap chain — used as the adjacency snap position.
        var chainLeft = snapTiles[0].center.x;
        var chainRight = snapTiles[snapTiles.length - 1].center.x + snapTiles[snapTiles.length - 1].dimensions.width;
        // Corner-align reference: when placing above/below the target row, use
        // closestTileData's own bounds (the specific tile being bordered), not the
        // full row. This keeps the ghost aligned with the tile directly above/below
        // rather than spanning to unrelated tiles in that row (e.g. a separate 1/10
        // at the far right of the same row as 1/2). When inserting into the SAME
        // row, use chain bounds.
        var isAboveBelow = Math.abs(targetY - closestTileData.center.y) > ROW_Y_TOL;
        // EVERY tile directly bordering this row — its bottom edge touching the
        // row's top, or its top edge touching the row's bottom — e.g. the
        // undivided "1" tile a fraction row is split under, AND the wider tile
        // in the row below that the row is stacked on top of.
        //
        // Collected as a list rather than picked with `.find()`: a row commonly
        // borders several tiles at once (a "1" above and a "1/3" below both
        // qualify), and any of their left/right edges is a legitimate corner the
        // user may be aligning to. `.find()` committed to whichever happened to
        // come first in `targets` order and silently discarded the rest, so a
        // drop 4px from the 1/3's right edge was rejected because only the "1"'s
        // edges had been considered.
        //
        // Also NOT filtered by horizontal overlap with the chain: a bordering
        // tile sitting entirely to the side of the current chain (e.g. a lone 1/8
        // at the far right of the row above a chain that starts at the far left)
        // is still a valid corner to align against. Direct edge contact with the
        // target row is what keeps this from matching unrelated tiles — a loose
        // vertical band would not.
        var borderTiles = targets
            .map(function (m) { return m.data; })
            .filter(function (t) {
            var touchesAbove = Math.abs(t.center.y + t.dimensions.height - targetY) < ROW_Y_TOL;
            var touchesBelow = Math.abs(t.center.y - (targetY + dims.height)) < ROW_Y_TOL;
            return touchesAbove || touchesBelow;
        });
        var govLeft_1 = borderTiles.length
            ? Math.min.apply(Math, __spreadArray([chainLeft], borderTiles.map(function (t) { return t.center.x; }), false)) : isAboveBelow
            ? closestTileData.center.x
            : chainLeft;
        var govRight_1 = borderTiles.length
            ? Math.max.apply(Math, __spreadArray([chainRight], borderTiles.map(function (t) { return t.center.x + t.dimensions.width; }), false)) : isAboveBelow
            ? closestTileData.center.x + closestTileData.dimensions.width
            : chainRight;
        // Bounding-box overlap (dragged tile's actual left/right edges vs. the
        // governing range) rather than center-in-range — a wide dragged tile can
        // have its center fall outside govLeft/govRight while its edges are
        // still legitimately within snapping distance.
        var withinChainBounds = checkRight >= govLeft_1 - SNAP_THRESHOLD && checkLeft <= govRight_1 + SNAP_THRESHOLD;
        // When inserting at the edges of a chain (before first or after last),
        // place the dragged tile next to the chain without recalculating existing
        // tile positions. This prevents floating-point drift from pushing tiles.
        // Also offer a corner-aligned alternative (left-align or right-align with
        // the chain bounds) so the tile can snap to any of the four corners
        // (top-left, top-right, bottom-left, bottom-right), whichever is nearest.
        if (insertIndex === 0) {
            var chainStartX = snapTiles[0].center.x - virtualItem.dimensions.width + BORDER_OVERLAP - groupLeftOffset;
            // Left-align with the governing row's left edge as an additional
            // candidate — but ONLY meaningful when bordering a genuinely separate
            // row above/below (isAboveBelow), where govLeft is that row's own
            // single-tile reference. For same-row insertion, govLeft === chainLeft,
            // so this candidate would mean "overlap the chain's own first tile" —
            // not a real position — and could incorrectly win over the correct
            // flush-attach position (chainStartX), producing an unwanted gap.
            var parentLeftX = govLeft_1 - groupLeftOffset;
            // Left-align candidate from the bordering tiles: the left edge of
            // whichever bordering tile is nearest the drop position. Every border
            // tile contributes (see `borderTiles` above) — not one "closest"
            // anchor and not only its own connected chain, which is what made the
            // catch radius depend on which qualifying tile happened to be found
            // first and on how many tiles already sat in the bordering row.
            var borderAnchors = borderTiles.length
                ? borderTiles
                : isAboveBelow
                    ? [closestTileData]
                    : [];
            var bestOtherRowCandidate = null;
            var bestOtherRowDist = Infinity;
            var bestOtherRowTile = null;
            for (var _k = 0, borderAnchors_1 = borderAnchors; _k < borderAnchors_1.length; _k++) {
                var t = borderAnchors_1[_k];
                var candidate = t.center.x - groupLeftOffset;
                var dist = Math.abs(pos.x - candidate);
                if (dist < bestOtherRowDist) {
                    bestOtherRowDist = dist;
                    bestOtherRowCandidate = candidate;
                    bestOtherRowTile = t;
                }
            }
            var otherRowWithinBounds = bestOtherRowCandidate !== null && bestOtherRowDist <= SNAP_THRESHOLD;
            var usedParentLeft = borderAnchors.length > 0 && Math.abs(pos.x - parentLeftX) <= Math.abs(pos.x - chainStartX);
            var leaderSnappedX_1 = usedParentLeft ? parentLeftX : chainStartX;
            // The tile whose geometry produced the winning candidate — reported as
            // `dockedToId` so the caller can match its scale (see SnapResult).
            var anchorTile = usedParentLeft
                ? ((_a = borderAnchors.find(function (t) { return Math.abs(t.center.x - govLeft_1) < ROW_Y_TOL; })) !== null && _a !== void 0 ? _a : borderAnchors[0])
                : snapTiles[0];
            if (bestOtherRowCandidate !== null && bestOtherRowDist < Math.abs(pos.x - leaderSnappedX_1)) {
                leaderSnappedX_1 = bestOtherRowCandidate;
                if (bestOtherRowTile)
                    anchorTile = bestOtherRowTile;
            }
            if ((!withinChainBounds && !otherRowWithinBounds) ||
                Math.abs(pos.x - leaderSnappedX_1) > SNAP_THRESHOLD) {
                return null;
            }
            return {
                x: leaderSnappedX_1,
                y: targetY,
                isSnappedX: true,
                isSnappedY: true,
                rotation: undefined,
                modifications: [],
                dockedToId: anchorTile === null || anchorTile === void 0 ? void 0 : anchorTile.id,
            };
        }
        if (insertIndex === snapTiles.length) {
            var lastTile = snapTiles[snapTiles.length - 1];
            var chainEndX = lastTile.center.x + lastTile.dimensions.width - BORDER_OVERLAP - groupLeftOffset;
            // Right-align with the governing row's right edge as an additional
            // candidate — same caveat as parentLeftX above: only meaningful when
            // bordering a separate row, not for same-row append.
            var parentRightX = govRight_1 - virtualItem.dimensions.width - groupLeftOffset;
            // Right-align candidate from the bordering tiles: the right edge of
            // whichever bordering tile is nearest the drop position — see the
            // prepend branch above for why every border tile contributes rather
            // than one "closest" anchor and its own connected chain.
            var borderAnchors = borderTiles.length
                ? borderTiles
                : isAboveBelow
                    ? [closestTileData]
                    : [];
            var bestOtherRowCandidate = null;
            var bestOtherRowDist = Infinity;
            var bestOtherRowTile = null;
            {
                for (var _l = 0, borderAnchors_2 = borderAnchors; _l < borderAnchors_2.length; _l++) {
                    var t = borderAnchors_2[_l];
                    var candidate = t.center.x + t.dimensions.width - virtualItem.dimensions.width - groupLeftOffset;
                    var dist = Math.abs(pos.x - candidate);
                    if (dist < bestOtherRowDist) {
                        bestOtherRowDist = dist;
                        bestOtherRowCandidate = candidate;
                        bestOtherRowTile = t;
                    }
                }
            }
            var otherRowWithinBounds = bestOtherRowCandidate !== null && bestOtherRowDist <= SNAP_THRESHOLD;
            var usedParentRight = borderAnchors.length > 0 && Math.abs(pos.x - parentRightX) <= Math.abs(pos.x - chainEndX);
            var leaderSnappedX_2 = usedParentRight ? parentRightX : chainEndX;
            // See the prepend branch: the tile whose geometry won, for `dockedToId`.
            var anchorTile = usedParentRight
                ? ((_b = borderAnchors.find(function (t) { return Math.abs(t.center.x + t.dimensions.width - govRight_1) < ROW_Y_TOL; })) !== null && _b !== void 0 ? _b : borderAnchors[0])
                : lastTile;
            if (bestOtherRowCandidate !== null && bestOtherRowDist < Math.abs(pos.x - leaderSnappedX_2)) {
                leaderSnappedX_2 = bestOtherRowCandidate;
                if (bestOtherRowTile)
                    anchorTile = bestOtherRowTile;
            }
            if ((!withinChainBounds && !otherRowWithinBounds) ||
                Math.abs(pos.x - leaderSnappedX_2) > SNAP_THRESHOLD) {
                return null;
            }
            return {
                x: leaderSnappedX_2,
                y: targetY,
                isSnappedX: true,
                isSnappedY: true,
                rotation: undefined,
                modifications: [],
                dockedToId: anchorTile === null || anchorTile === void 0 ? void 0 : anchorTile.id,
            };
        }
        // Mid-chain insertion: recalculate positions to make room
        var ordered = __spreadArray(__spreadArray(__spreadArray([], snapTiles.slice(0, insertIndex), true), [
            virtualItem
        ], false), snapTiles.slice(insertIndex), true);
        var refX = ordered[0].center.x;
        var modifications = [];
        var leaderSnappedX = pos.x;
        var currentX = refX;
        var _loop_3 = function (item) {
            if (item.id === 'NEW' || item.id === 'NEW_GROUP') {
                leaderSnappedX = currentX - groupLeftOffset;
            }
            else {
                // Check if this item is locked. If it's locked and needs to move, the snap is invalid.
                var originalItem = snapTiles.find(function (rt) { return rt.id === item.id; });
                if (originalItem && ((_c = originalItem.lock) === null || _c === void 0 ? void 0 : _c.positioning)) {
                    if (Math.abs(originalItem.center.x - currentX) > EPSILON) {
                        return { value: null };
                    }
                }
                if (originalItem && Math.abs(originalItem.center.x - currentX) > EPSILON) {
                    modifications.push({
                        id: item.id,
                        center: { x: currentX, y: targetY },
                    });
                }
            }
            currentX += item.dimensions.width - BORDER_OVERLAP;
        };
        for (var _m = 0, ordered_1 = ordered; _m < ordered_1.length; _m++) {
            var item = ordered_1[_m];
            var state_2 = _loop_3(item);
            if (typeof state_2 === "object")
                return state_2.value;
        }
        // --- STRICT HORIZONTAL CHECK ---
        if (Math.abs(pos.x - leaderSnappedX) > SNAP_THRESHOLD) {
            return null;
        }
        return {
            x: leaderSnappedX,
            y: targetY,
            isSnappedX: true,
            isSnappedY: true,
            rotation: undefined,
            modifications: modifications,
            // Inserting into the middle of this chain — match the chain's scale.
            dockedToId: (_d = snapTiles[0]) === null || _d === void 0 ? void 0 : _d.id,
        };
    }
    else {
        // No tiles in the target row. Offer four corner snap positions relative to the
        // overall bounds of all target tiles (the row above or below):
        //   left-align  → dragged tile's left edge = leftmost target's left edge
        //   right-align → dragged tile's right edge = rightmost target's right edge
        // Pick whichever is nearest to the drop position.
        // Use the bounds of the CONNECTED CHAIN closestTileData belongs to (not
        // just that one tile) as the corner reference. A single tile's bounds
        // works fine when the dragged tile is a similar size, but collapses when
        // the dragged tile spans an entire row of much smaller tiles (e.g. a "1"
        // tile above a row of twelve "1/12" tiles): closestTileData ends up
        // being whichever small tile is nearest, and right-aligning the wide
        // dragged tile to that one tile's right edge overshoots past the whole
        // row — so it always appears to "snap left" regardless of where it was
        // actually dropped. Using the full chain's bounds instead makes the
        // corner-align reference match the whole visual row.
        var chainRowTiles = targets
            .map(function (t) { return t.data; })
            .filter(function (d) { return Math.abs(d.center.y - closestTileData.center.y) < ROW_Y_TOL; })
            .sort(function (a, b) { return a.center.x - b.center.x; });
        var closestChain = [closestTileData];
        var currentChain = [];
        for (var _o = 0, chainRowTiles_1 = chainRowTiles; _o < chainRowTiles_1.length; _o++) {
            var tile = chainRowTiles_1[_o];
            if (currentChain.length === 0) {
                currentChain = [tile];
            }
            else {
                var prevRightEdge = currentChain[currentChain.length - 1].center.x +
                    currentChain[currentChain.length - 1].dimensions.width;
                if (Math.abs(prevRightEdge - tile.center.x) < CHAIN_TOL) {
                    currentChain.push(tile);
                }
                else {
                    if (currentChain.some(function (t) { return t.id === closestTileData.id; }))
                        closestChain = currentChain;
                    currentChain = [tile];
                }
            }
        }
        if (currentChain.some(function (t) { return t.id === closestTileData.id; }))
            closestChain = currentChain;
        var refLeft = closestChain[0].center.x;
        var refRight = closestChain[closestChain.length - 1].center.x +
            closestChain[closestChain.length - 1].dimensions.width;
        var withinBounds = checkRight >= refLeft - SNAP_THRESHOLD && checkLeft <= refRight + SNAP_THRESHOLD;
        if (!withinBounds)
            return null;
        // Candidates: align the dragged tile's left edge OR right edge with
        // EVERY individual tile's left/right edge in the chain — not just the
        // chain's two outer corners. This lets a tile wider than a single row
        // tile (but not necessarily as wide as the whole row) "hang" flush
        // against any block boundary, e.g. a "1" tile dropped near the 3rd block
        // of a "1/12" row can align its left edge there and overhang the row's
        // right side, rather than only ever landing at the row's far corners.
        var candidates = [];
        for (var _p = 0, closestChain_1 = closestChain; _p < closestChain_1.length; _p++) {
            var tile = closestChain_1[_p];
            candidates.push(tile.center.x - groupLeftOffset);
            candidates.push(tile.center.x + tile.dimensions.width - virtualItem.dimensions.width - groupLeftOffset);
        }
        var leaderSnappedX = candidates[0];
        var minCandidateDist = Math.abs(pos.x - candidates[0]);
        var anchorTile = closestChain[0];
        for (var _q = 0, closestChain_2 = closestChain; _q < closestChain_2.length; _q++) {
            var tile = closestChain_2[_q];
            for (var _r = 0, _s = [
                tile.center.x - groupLeftOffset,
                tile.center.x + tile.dimensions.width - virtualItem.dimensions.width - groupLeftOffset,
            ]; _r < _s.length; _r++) {
                var candidate = _s[_r];
                var dist = Math.abs(pos.x - candidate);
                if (dist < minCandidateDist) {
                    minCandidateDist = dist;
                    leaderSnappedX = candidate;
                    anchorTile = tile;
                }
            }
        }
        return {
            x: leaderSnappedX,
            y: targetY,
            isSnappedX: true,
            isSnappedY: true,
            rotation: undefined,
            modifications: [],
            dockedToId: anchorTile === null || anchorTile === void 0 ? void 0 : anchorTile.id,
        };
    }
};
/**
 * Checks if two angular intervals overlap.
 * Angles in degrees [0, 360).
 */
var checkCircularOverlap = function (s1, sw1, s2, sw2) {
    // Normalize to [0, 360)
    var norm = function (a) { return ((a % 360) + 360) % 360; };
    // Relative start of 1 from 2
    var r1 = norm(s1 - s2);
    // If start of 1 is strictly inside 2
    if (r1 > EPSILON && r1 < sw2 - EPSILON)
        return true;
    // Relative end of 1 from 2
    var e1 = norm(s1 + sw1 - s2);
    if (e1 > EPSILON && e1 < sw2 - EPSILON)
        return true;
    // Check if 2 is inside 1 (case where 1 engulfs 2 completely)
    var r2 = norm(s2 - s1);
    if (r2 > EPSILON && r2 < sw1 - EPSILON)
        return true;
    return false;
};
/**
 * Resolve where a dragged NMP piece lands when the background grid is OFF.
 *
 * Classification is **center-based**: the dragged piece's center decides both
 * whether a neighbour is a snap candidate at all and which of its edges the
 * piece belongs to.
 *
 *  - `overlap` — the center falls inside the candidate's bounds, i.e. inside one
 *    of its internal grid cells. The piece stacks there, grid-aligned to that
 *    cell.
 *  - `above` / `below` / `left` / `right` — the center is past exactly one of
 *    the candidate's edges while the two pieces still span each other on the
 *    perpendicular axis, and that edge is within `ADJACENT_SNAP_GAP` (from
 *    either side). The piece lands flush against that edge.
 *  - anything else — no snap; the drop position is returned unchanged. A
 *    diagonal drop puts the center past two edges at once, so it never
 *    qualifies, whether or not it slightly overlaps the candidate's corner.
 *
 * @param cx        dragged center x (drop position)
 * @param cy        dragged center y
 * @param w         dragged scaled width
 * @param h         dragged scaled height
 * @param dragScale dragged piece's own scale (bounds the adjacency allowance)
 */
export var solveNmpPieceSnap = function (cx, cy, w, h, candidates, dragScale) {
    if (dragScale === void 0) { dragScale = 1; }
    var topLeftX = cx - w / 2;
    var topLeftY = cy - h / 2;
    var myRight = topLeftX + w;
    var myBottom = topLeftY + h;
    var bestDist = Infinity;
    var bestTarget = null;
    var _loop_4 = function (other) {
        var oTLX_1 = other.tlX, oTLY_1 = other.tlY, ow_1 = other.w, oh_1 = other.h;
        var oRight = oTLX_1 + ow_1;
        var oBottom = oTLY_1 + oh_1;
        var oCx = oTLX_1 + ow_1 / 2;
        var oCy = oTLY_1 + oh_1 / 2;
        var gapX = Math.max(0, Math.max(topLeftX - oRight, oTLX_1 - myRight));
        var gapY = Math.max(0, Math.max(topLeftY - oBottom, oTLY_1 - myBottom));
        // Adjacency gap allowance scales with the smaller of the two pieces so a
        // large target can't snap from further away than its own grid step implies.
        var adjGap = ADJACENT_SNAP_GAP * Math.min(dragScale, other.scale);
        // Does the drop sit within the target's own edge-to-edge span? Tested
        // symmetrically — either center inside the other piece's extent — so that
        // flush adjacency doesn't depend on which of the two pieces is dragged
        // (a tall `tens` beside a single `ones` must snap either way round).
        var alignedX = (cx >= oTLX_1 && cx <= oRight) || (oCx >= topLeftX && oCx <= myRight);
        var alignedY = (cy >= oTLY_1 && cy <= oBottom) || (oCy >= topLeftY && oCy <= myBottom);
        // Overlap (stacking on the target) is deliberately NOT symmetric: it
        // requires the *dragged* center to land inside the target.
        var centerInsideX = cx >= oTLX_1 && cx <= oRight;
        var centerInsideY = cy >= oTLY_1 && cy <= oBottom;
        // An adjacent drop must be near the edge it snaps to, from either side —
        // a slight overlap still reads as "flush against this edge", but a piece
        // buried deep inside the target must not be ejected to that edge.
        var nearEdge = function (signedGap) { return Math.abs(signedGap) <= adjGap; };
        var mode_1 = null;
        if (centerInsideX && centerInsideY) {
            mode_1 = 'overlap';
        }
        else if (alignedX && cy > oBottom && nearEdge(topLeftY - oBottom)) {
            mode_1 = 'below';
        }
        else if (alignedX && cy < oTLY_1 && nearEdge(oTLY_1 - myBottom)) {
            mode_1 = 'above';
        }
        else if (alignedY && cx > oRight && nearEdge(topLeftX - oRight)) {
            mode_1 = 'right';
        }
        else if (alignedY && cx < oTLX_1 && nearEdge(oTLX_1 - myRight)) {
            mode_1 = 'left';
        }
        // No case matched — a diagonal drop, a drop too far from any edge, or one
        // overlapping the target so deeply that no single edge owns it. All are
        // left where the user dropped them.
        if (!mode_1)
            return "continue";
        var dist = Math.sqrt(gapX * gapX + gapY * gapY);
        if (dist < bestDist) {
            bestDist = dist;
            bestTarget = __assign(__assign({}, other), { mode: mode_1 });
        }
    };
    for (var _i = 0, candidates_2 = candidates; _i < candidates_2.length; _i++) {
        var other = candidates_2[_i];
        _loop_4(other);
    }
    if (!bestTarget)
        return { x: cx, y: cy };
    var oTLX = bestTarget.tlX, oTLY = bestTarget.tlY, ow = bestTarget.w, oh = bestTarget.h, targetScale = bestTarget.scale, mode = bestTarget.mode;
    // Grid-relative rounding — use the target's scaled NMP grid step so the piece
    // lands on the target's internal grid lines, not just its border.
    var gridStep = PIECE_GRID * targetScale;
    var snappedTLX = oTLX + Math.round((topLeftX - oTLX) / gridStep) * gridStep;
    var snappedTLY = oTLY + Math.round((topLeftY - oTLY) / gridStep) * gridStep;
    // Clamp to prevent gaps: keep the piece touching or overlapping the target.
    snappedTLX = Math.max(oTLX - w, Math.min(oTLX + ow, snappedTLX));
    snappedTLY = Math.max(oTLY - h, Math.min(oTLY + oh, snappedTLY));
    // Adjacent placement: pin the primary axis flush to the target's edge and
    // keep the perpendicular position within the target's extent so the two line
    // up instead of sitting at a diagonal corner. Overlapping drops keep their
    // grid-rounded position so a piece can stack on any internal grid line.
    if (mode === 'below' || mode === 'above') {
        snappedTLY = mode === 'below' ? oTLY + oh : oTLY - h;
        snappedTLX = Math.max(oTLX, Math.min(oTLX + ow - w, snappedTLX));
    }
    else if (mode === 'right' || mode === 'left') {
        snappedTLX = mode === 'right' ? oTLX + ow : oTLX - w;
        snappedTLY = Math.max(oTLY, Math.min(oTLY + oh - h, snappedTLY));
    }
    // An 'overlap' snap that lands the dragged piece on exactly the same cell
    // as the target (same size, zero offset) would completely hide the target
    // underneath it. Any other grid-aligned cell — including a different
    // internal cell of a same-size candidate (e.g. tens-onto-tens landing on
    // cell 1 instead of cell 0) — only partially overlaps and is unaffected;
    // leave the piece at its raw drop position instead of snapping in this one
    // exact-coincidence case.
    if (mode === 'overlap' && w === ow && h === oh && snappedTLX === oTLX && snappedTLY === oTLY) {
        return { x: cx, y: cy };
    }
    return { x: snappedTLX + w / 2, y: snappedTLY + h / 2 };
};
/**
 * Snap a position to the background square grid.
 * Takes any x/y and returns the nearest grid-aligned x/y.
 */
export var snapToBackgroundGrid = function (x, y, BG_GRID_SIZE) {
    if (BG_GRID_SIZE === void 0) { BG_GRID_SIZE = GRID_SIZE; }
    return {
        x: Math.round(x / BG_GRID_SIZE) * BG_GRID_SIZE,
        y: Math.round(y / BG_GRID_SIZE) * BG_GRID_SIZE,
    };
};
/**
 * Snap a position to the nearest vertex of the equilateral triangle grid.
 * Vertices alternate between even rows (x = col*S) and odd rows (x = col*S + S/2).
 */
export var snapToTriangleGrid = function (x, y) {
    var S = GRID_SIZE;
    var H = (S * Math.sqrt(3)) / 2;
    var rowBelow = Math.floor(y / H);
    var bestDist = Infinity;
    var best = { x: x, y: y };
    for (var _i = 0, _a = [rowBelow, rowBelow + 1]; _i < _a.length; _i++) {
        var row = _a[_i];
        var vy = row * H;
        var xOffset = row % 2 === 0 ? 0 : S / 2;
        var col = Math.round((x - xOffset) / S);
        var vx = col * S + xOffset;
        var dist = Math.hypot(x - vx, y - vy);
        if (dist < bestDist) {
            bestDist = dist;
            best = { x: vx, y: vy };
        }
    }
    return best;
};
